import { useEffect, useState, useCallback, useRef } from "react";
import {
  DEFAULT_QUESTS,
  PRESET_AVATARS,
  todayKey,
  yesterdayKey,
  daysBetween,
  applyXpGain,
  checkNewAchievements,
} from "../lib/gameLogic";

const STORAGE_KEY = "solo_leveling_fitness_v1";

const initialState = () => ({
  username: "",
  avatarId: PRESET_AVATARS[0].id,
  level: 1,
  xp: 0,
  streak: 0,
  bestStreak: 0,
  totalWorkouts: 0,
  totalXp: 0,
  quests: DEFAULT_QUESTS.map((q) => ({ ...q, done: false })),
  lastQuestDate: todayKey(),
  lastCompleteDay: null, // last day "Complete Day" was pressed
  completedDays: {}, // { 'YYYY-MM-DD': true }
  history: [], // [{ date, xp, questsCount, level, rank }]
  achievements: {}, // { id: ISO date unlocked }
  customQuestCreated: false,
  onboarded: false,
});

const safeLoad = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return initialState();
    const parsed = JSON.parse(raw);
    return { ...initialState(), ...parsed };
  } catch {
    return initialState();
  }
};

export default function useGameState() {
  const [state, setState] = useState(safeLoad);
  const [lastEvent, setLastEvent] = useState(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  // Persist
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // ignore
    }
  }, [state]);

  // Achievement watcher: auto-unlock & publish toasts for non-completeDay paths
  // (e.g., Quest Forger fires when user creates a custom quest).
  const [achievementToast, setAchievementToast] = useState(null);
  useEffect(() => {
    const newly = checkNewAchievements(state);
    if (newly.length === 0) return;
    const nowIso = new Date().toISOString();
    setState((s) => ({
      ...s,
      achievements: {
        ...(s.achievements || {}),
        ...Object.fromEntries(newly.map((a) => [a.id, nowIso])),
      },
    }));
    setAchievementToast(newly);
  }, [state]);
  const clearAchievementToast = useCallback(
    () => setAchievementToast(null),
    [],
  );

  // Daily reset on mount and when day changes
  useEffect(() => {
    const tk = todayKey();
    if (state.lastQuestDate !== tk) {
      // Check streak penalty
      let newStreak = state.streak;
      if (state.lastCompleteDay) {
        const diff = daysBetween(state.lastCompleteDay, tk);
        if (diff > 1) {
          newStreak = 0;
        }
      }
      setState((s) => ({
        ...s,
        quests: s.quests.map((q) => ({ ...q, done: false })),
        lastQuestDate: tk,
        streak: newStreak,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setUsername = (username) => setState((s) => ({ ...s, username }));
  const setAvatar = (avatarId) => setState((s) => ({ ...s, avatarId }));
  const completeOnboarding = (username, avatarId) =>
    setState((s) => ({ ...s, username, avatarId, onboarded: true }));

  const toggleQuest = (id) =>
    setState((s) => ({
      ...s,
      quests: s.quests.map((q) => (q.id === id ? { ...q, done: !q.done } : q)),
    }));

  const addQuest = (q) =>
    setState((s) => ({
      ...s,
      customQuestCreated: true,
      quests: [
        ...s.quests,
        {
          id: `q_${Date.now()}`,
          title: q.title,
          xp: q.xp,
          icon: q.icon || "Sword",
          done: false,
        },
      ],
    }));

  const editQuest = (id, patch) =>
    setState((s) => ({
      ...s,
      quests: s.quests.map((q) => (q.id === id ? { ...q, ...patch } : q)),
    }));

  const removeQuest = (id) =>
    setState((s) => ({ ...s, quests: s.quests.filter((q) => q.id !== id) }));

  const completeDay = useCallback(() => {
    // Compute everything synchronously from the current state via ref —
    // avoids any race/double-invocation issues with React 18 setState updaters.
    const s = stateRef.current;
    const completedQuests = s.quests.filter((q) => q.done);
    if (completedQuests.length === 0) return null;
    const gain = completedQuests.reduce((acc, q) => acc + q.xp, 0);
    const tk = todayKey();
    const yk = yesterdayKey();
    const alreadyToday = s.lastCompleteDay === tk;
    let newStreak = s.streak;
    if (!alreadyToday) {
      if (s.lastCompleteDay === yk) newStreak = s.streak + 1;
      else if (s.lastCompleteDay === tk) newStreak = s.streak;
      else newStreak = 1;
    }
    const xpRes = applyXpGain({ level: s.level, xp: s.xp }, gain);
    const next = {
      ...s,
      xp: xpRes.xp,
      level: xpRes.level,
      totalXp: s.totalXp + gain,
      totalWorkouts: s.totalWorkouts + completedQuests.length,
      streak: newStreak,
      lastCompleteDay: tk,
      completedDays: { ...s.completedDays, [tk]: true },
      quests: s.quests.map((q) => ({ ...q, done: false })),
    };
    const result = {
      gain,
      leveledUp: xpRes.leveledUp,
      rankedUp: xpRes.rankedUp,
      oldLevel: xpRes.oldLevel,
      newLevel: xpRes.level,
      oldRank: xpRes.oldRank,
      newRank: xpRes.newRank,
      questsCompleted: completedQuests.length,
    };
    stateRef.current = next;
    setState(next);
    setLastEvent(result);
    return result;
  }, []);

  const clearLastEvent = useCallback(() => setLastEvent(null), []);

  const resetAll = () => {
    localStorage.removeItem(STORAGE_KEY);
    setState(initialState());
  };

  return {
    state,
    lastEvent,
    clearLastEvent,
    achievementToast,
    clearAchievementToast,
    setUsername,
    setAvatar,
    completeOnboarding,
    toggleQuest,
    addQuest,
    editQuest,
    removeQuest,
    completeDay,
    resetAll,
  };
}
